/**
 * WeeklyProgressChart - Visual overview of training progression across weeks
 *
 * Shows:
 * - Total Distance (km) per week
 * - Total Time (hours) per week
 * - Total TSS per week
 *
 * Helps athletes visualize incremental changes in training load.
 */

import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { TrainingPlan, Workout } from '@shared/types';
import React, { useMemo, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { toISODateString } from '../../utils/dateUtils';
import { getAllWorkouts } from '../../utils/planUtils';

interface WeeklyProgressChartProps {
  trainingPlan: TrainingPlan | null;
  activeWeekNumber?: number; // The selected/viewed week number (1-based)
}

interface WeekData {
  weekLabel: string;
  weekNumber: number;
  totalDistance: number;
  runningDistance: number;
  cyclingDistance: number;
  totalTime: number; // in minutes
  totalTSS: number;
  workoutCount: number;
}

/**
 * Calculate weekly statistics from training plan
 */
function calculateWeeklyData(trainingPlan: TrainingPlan | null): WeekData[] {
  const allWorkouts = getAllWorkouts(trainingPlan);
  if (allWorkouts.length === 0) {
    return [];
  }

  // Group workouts by week (Monday-Sunday)
  const weekMap = new Map<string, Workout[]>();

  allWorkouts.forEach(workout => {
    if (!workout.training_date) return;

    const date = new Date(workout.training_date);
    const dayOfWeek = date.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Monday = start of week
    const monday = new Date(date);
    monday.setDate(date.getDate() + diff);
    monday.setHours(0, 0, 0, 0);

    const weekKey = toISODateString(monday);
    if (!weekMap.has(weekKey)) {
      weekMap.set(weekKey, []);
    }
    weekMap.get(weekKey)!.push(workout);
  });

  // Convert to sorted array of week data
  const weeks = Array.from(weekMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([_weekKey, workouts], index) => {
      const activeWorkouts = workouts.filter(w => (w.name || '').toLowerCase() !== 'rest');

      const weekLabel = `W${index + 1}`;

      // Calculate totals
      let runningDistance = 0;
      let cyclingDistance = 0;
      let totalTime = 0;
      let totalTSS = 0;

      activeWorkouts.forEach(workout => {
        const isCycling = workout.sportType === 'cycling';

        if (isCycling) {
          cyclingDistance += workout.total_distance || 0;
          totalTime += workout.total_duration || 0;
          totalTSS += workout.TSS || 0;
        } else {
          runningDistance += workout.total_distance || 0;
          totalTime += workout.total_duration || 0;
          totalTSS += workout.TSS || 0;
        }
      });

      return {
        weekLabel,
        weekNumber: index + 1,
        totalDistance: Math.round((runningDistance + cyclingDistance) * 10) / 10,
        runningDistance: Math.round(runningDistance * 10) / 10,
        cyclingDistance: Math.round(cyclingDistance * 10) / 10,
        totalTime: Math.round(totalTime),
        totalTSS: Math.round(totalTSS),
        workoutCount: activeWorkouts.length,
      };
    });

  return weeks;
}

/**
 * Format time from minutes to hours:minutes
 */
function formatTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins}m`;
  return `${hours}h ${mins}m`;
}

export const WeeklyProgressChart: React.FC<WeeklyProgressChartProps> = ({
  trainingPlan,
  activeWeekNumber,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expanded, setExpanded] = useState(false);

  const weeklyData = useMemo(() => calculateWeeklyData(trainingPlan), [trainingPlan]);

  // Calculate plan totals for summary
  const planTotals = useMemo(() => {
    return weeklyData.reduce(
      (acc, week) => ({
        totalDistance: acc.totalDistance + week.totalDistance,
        totalTime: acc.totalTime + week.totalTime,
        totalTSS: acc.totalTSS + week.totalTSS,
        totalWorkouts: acc.totalWorkouts + week.workoutCount,
      }),
      { totalDistance: 0, totalTime: 0, totalTSS: 0, totalWorkouts: 0 }
    );
  }, [weeklyData]);

  if (weeklyData.length === 0) {
    return null;
  }

  // Calculate percentage change between first and last week
  const firstWeek = weeklyData[0];
  const lastWeek = weeklyData[weeklyData.length - 1];
  const distanceChange =
    firstWeek.totalDistance > 0
      ? Math.round(
          ((lastWeek.totalDistance - firstWeek.totalDistance) / firstWeek.totalDistance) * 100
        )
      : 0;
  const tssChange =
    firstWeek.totalTSS > 0
      ? Math.round(((lastWeek.totalTSS - firstWeek.totalTSS) / firstWeek.totalTSS) * 100)
      : 0;

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{
        mb: 2,
        '&:before': { display: 'none' },
        boxShadow: 1,
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls="weekly-progress-content"
        id="weekly-progress-header"
        sx={{
          bgcolor: 'background.paper',
          borderBottom: expanded ? 1 : 0,
          borderColor: 'divider',
        }}
      >
        <Box display="flex" alignItems="center" gap={2} width="100%" flexWrap="wrap">
          <Typography variant="subtitle2" fontWeight={600}>
            📈 Training Progression
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Chip
              label={`${Math.round(planTotals.totalDistance)} km`}
              size="small"
              color="primary"
              variant="outlined"
            />
            <Chip
              label={formatTime(planTotals.totalTime)}
              size="small"
              color="success"
              variant="outlined"
            />
            <Chip
              label={`${planTotals.totalTSS} TSS`}
              size="small"
              color="warning"
              variant="outlined"
            />
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
        {/* Progression indicators */}
        <Box display="flex" gap={2} mb={2} flexWrap="wrap" justifyContent="center">
          <Box textAlign="center">
            <Typography variant="caption" color="text.secondary">
              Distance Change
            </Typography>
            <Typography
              variant="h6"
              color={distanceChange >= 0 ? 'success.main' : 'error.main'}
              fontWeight={600}
            >
              {distanceChange >= 0 ? '+' : ''}
              {distanceChange}%
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="caption" color="text.secondary">
              TSS Change
            </Typography>
            <Typography
              variant="h6"
              color={tssChange >= 0 ? 'success.main' : 'error.main'}
              fontWeight={600}
            >
              {tssChange >= 0 ? '+' : ''}
              {tssChange}%
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="caption" color="text.secondary">
              Total Weeks
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {weeklyData.length}
            </Typography>
          </Box>
        </Box>

        {/* Chart - only render when expanded to avoid ResponsiveContainer measuring 0-height container */}
        <Box height={isMobile ? 250 : 300}>
          {expanded && (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={weeklyData}
                margin={{
                  top: 10,
                  right: isMobile ? 10 : 30,
                  left: isMobile ? -10 : 10,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis
                  dataKey="weekLabel"
                  stroke={theme.palette.text.secondary}
                  tick={{ fill: theme.palette.text.secondary, fontSize: isMobile ? 10 : 12 }}
                />
                <YAxis
                  yAxisId="left"
                  stroke={theme.palette.primary.main}
                  tick={{ fill: theme.palette.text.secondary, fontSize: isMobile ? 10 : 12 }}
                  label={
                    !isMobile
                      ? {
                          value: 'Distance (km)',
                          angle: -90,
                          position: 'insideLeft',
                          style: { fill: theme.palette.text.secondary, fontSize: 11 },
                        }
                      : undefined
                  }
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke={theme.palette.warning.main}
                  tick={{ fill: theme.palette.text.secondary, fontSize: isMobile ? 10 : 12 }}
                  label={
                    !isMobile
                      ? {
                          value: 'TSS',
                          angle: 90,
                          position: 'insideRight',
                          style: { fill: theme.palette.text.secondary, fontSize: 11 },
                        }
                      : undefined
                  }
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 4,
                  }}
                  labelStyle={{ color: theme.palette.text.primary }}
                  formatter={(value: number, name: string) => {
                    if (name === 'Time (h)') {
                      return [formatTime(value * 60), name];
                    }
                    return [value, name];
                  }}
                />
                <Legend wrapperStyle={{ fontSize: isMobile ? 10 : 12 }} />
                <Bar
                  yAxisId="left"
                  dataKey="totalDistance"
                  name="Distance (km)"
                  radius={[4, 4, 0, 0]}
                >
                  {weeklyData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        entry.weekNumber === activeWeekNumber
                          ? theme.palette.secondary.main
                          : theme.palette.primary.main
                      }
                      fillOpacity={entry.weekNumber === activeWeekNumber ? 1 : 0.7}
                      stroke={
                        entry.weekNumber === activeWeekNumber
                          ? theme.palette.secondary.dark
                          : 'none'
                      }
                      strokeWidth={entry.weekNumber === activeWeekNumber ? 2 : 0}
                    />
                  ))}
                </Bar>
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="totalTSS"
                  stroke={theme.palette.warning.main}
                  strokeWidth={2}
                  dot={{ r: 4, fill: theme.palette.warning.main }}
                  name="TSS"
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey={(d: WeekData) => Math.round((d.totalTime / 60) * 10) / 10}
                  stroke={theme.palette.success.main}
                  strokeWidth={2}
                  dot={{ r: 4, fill: theme.palette.success.main }}
                  name="Time (h)"
                  strokeDasharray="5 5"
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </Box>

        {/* Weekly breakdown table for mobile */}
        {isMobile && (
          <Box mt={2}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Weekly Breakdown
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr 1fr 1fr',
                gap: 0.5,
                fontSize: '0.75rem',
              }}
            >
              <Typography variant="caption" fontWeight={600}>
                Week
              </Typography>
              <Typography variant="caption" fontWeight={600} textAlign="right">
                Dist
              </Typography>
              <Typography variant="caption" fontWeight={600} textAlign="right">
                Time
              </Typography>
              <Typography variant="caption" fontWeight={600} textAlign="right">
                TSS
              </Typography>
              {weeklyData.map(week => (
                <React.Fragment key={week.weekNumber}>
                  <Typography variant="caption">{week.weekLabel}</Typography>
                  <Typography variant="caption" textAlign="right">
                    {week.totalDistance} km
                  </Typography>
                  <Typography variant="caption" textAlign="right">
                    {formatTime(week.totalTime)}
                  </Typography>
                  <Typography variant="caption" textAlign="right">
                    {week.totalTSS}
                  </Typography>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default WeeklyProgressChart;
